/**
 * QuotaCoinNotification
 * 3D coin flip animation that appears when a quota is consumed.
 * Front face: quota type icon + label. Back face: usage stats.
 */

import { useQuotaCoin } from '@contexts/QuotaCoinContext';
import type { QuotaCoinType } from '@parlay-picker/shared/quotaCoin';
import { AnimatePresence, motion } from 'framer-motion';
import { BarChart3, Crosshair, Target } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';

const COIN_ICONS: Record<QuotaCoinType, ReactElement> = {
  pick: <Crosshair className="w-6 h-6" />,
  analyze: <BarChart3 className="w-6 h-6" />,
  track: <Target className="w-6 h-6" />,
};

const COIN_LABELS: Record<QuotaCoinType, string> = {
  pick: 'PICK',
  analyze: 'ANALYZE',
  track: 'TRACK',
};

export function QuotaCoinNotification(): ReactElement | null {
  const { coin, dismissCoin } = useQuotaCoin();
  // Force re-mount on each new coin to restart CSS animation
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    if (coin) setAnimKey((k) => k + 1);
  }, [coin]);

  return (
    <AnimatePresence>
      {coin && (
        <motion.div
          key={animKey}
          initial={{ x: 120, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="fixed bottom-6 right-6 z-[9999] cursor-pointer"
          onClick={dismissCoin}
          role="status"
          aria-live="polite"
          aria-label={`${COIN_LABELS[coin.type]} quota: ${coin.used} used, ${coin.remaining} remaining`}
        >
          {/* Coin container */}
          <div className="coin-flip-container w-20 h-20">
            <div className="coin-flip-inner w-full h-full relative">
              {/* Front face — quota type */}
              <div className="coin-face-front bg-gradient-to-br from-theme-primary via-theme-primary-hover to-theme-secondary shadow-lg shadow-theme-primary/30">
                <div className="flex flex-col items-center gap-0.5 text-white">
                  {COIN_ICONS[coin.type]}
                  <span className="text-[9px] font-bold tracking-wider">
                    {COIN_LABELS[coin.type]}
                  </span>
                </div>
                {/* Metallic sheen overlay */}
                <div className="coin-shimmer absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-white/20 to-transparent pointer-events-none" />
              </div>

              {/* Back face — usage stats */}
              <div className="coin-face-back bg-gradient-to-br from-theme-surface via-theme-surface-hover to-theme-bg-tertiary border-2 border-theme-primary/40 shadow-lg shadow-theme-primary/20">
                <div className="flex flex-col items-center gap-0.5 px-1">
                  <span className="text-[10px] font-bold text-theme-primary">{coin.used} used</span>
                  <div className="w-6 h-px bg-theme-border" />
                  <span className="text-[10px] font-semibold text-theme-text-secondary">
                    {coin.remaining} left
                  </span>
                  {coin.mode && (
                    <span className="text-[8px] text-theme-text-tertiary uppercase tracking-wide">
                      {coin.mode}
                    </span>
                  )}
                </div>
                {/* Metallic sheen overlay */}
                <div className="coin-shimmer absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-white/10 to-transparent pointer-events-none" />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
